import * as blazeface from '@tensorflow-models/blazeface';
import '@tensorflow/tfjs-backend-webgl';
import * as tf from '@tensorflow/tfjs';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import type { EmotionProbabilities } from '@/types/emotion';

type UseEmotionEngineArgs = {
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  onPrediction: (probabilities: EmotionProbabilities) => void;
  smoothingWindow?: number;
  aggregationMs?: number;
  modelUrl?: string;
};

export const useEmotionEngine = ({
  videoRef,
  onPrediction,
  smoothingWindow = 5,
  aggregationMs = 1000,
  modelUrl = '/models/emotion-model/model.json'
}: UseEmotionEngineArgs) => {
  const [isReady, setIsReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [debugEmotions, setDebugEmotions] = useState<EmotionProbabilities | null>(null);
  
  const frameBuffer = useRef<EmotionProbabilities[]>([]);
  const faceDetectorRef = useRef<blazeface.BlazeFaceModel | null>(null);
  const modelRef = useRef<tf.GraphModel | null>(null);
  const rafRef = useRef<number>();
  const lastEmitRef = useRef<number>(Date.now());
  const lastRunRef = useRef<number>(0);
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let isMounted = true;

    const startProcessingLoop = () => {
        const processFrame = async () => {
            if (!isMounted) return;

            // 1. SAFETY CHECK: Ensure Backend is Alive
            // This prevents the "backend undefined" crash
            try {
                if (!tf.getBackend()) {
                    console.warn("WebGL Backend lost, resetting...");
                    await tf.setBackend('webgl');
                    await tf.ready();
                }
            } catch (e) {
                // If checking the backend fails, skip this frame
                rafRef.current = requestAnimationFrame(processFrame);
                return;
            }

            const now = Date.now();
            if (now - lastRunRef.current < 100) {
                rafRef.current = requestAnimationFrame(processFrame);
                return;
            }
            lastRunRef.current = now;

            const video = videoRef.current;
            const detector = faceDetectorRef.current;
            const model = modelRef.current;

            if (!video || !detector || !model || video.readyState < 2) {
                rafRef.current = requestAnimationFrame(processFrame);
                return;
            }

            if (video.videoWidth > 0 && video.width !== video.videoWidth) {
                video.width = video.videoWidth;
                video.height = video.videoHeight;
            }

            if (!offscreenCanvasRef.current) {
                offscreenCanvasRef.current = document.createElement('canvas');
            }
            const canvas = offscreenCanvasRef.current;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });

            if (ctx) {
                try {
                    const returnTensors = false;
                    const predictions = await detector.estimateFaces(video, returnTensors);

                    if (predictions.length > 0) {
                        const start = predictions[0].topLeft as [number, number];
                        const end = predictions[0].bottomRight as [number, number];
                        const width = end[0] - start[0];
                        const height = end[1] - start[1];
                        const x = Math.max(0, start[0]);
                        const y = Math.max(0, start[1]);

                        const probs = tf.tidy(() => {
                            canvas.width = 48; 
                            canvas.height = 48;
                            ctx.drawImage(video, x, y, width, height, 0, 0, 48, 48);

                            const imageTensor = tf.browser.fromPixels(canvas)
                                .toFloat()
                                .mean(2)
                                .expandDims(-1)
                                .expandDims(0)
                                .div(255);

                            const result = model.predict(imageTensor) as tf.Tensor;
                            return Array.from(result.dataSync());
                        });

                        const raw: EmotionProbabilities = {
                            angry: probs[0] ?? 0,
                            disgust: probs[1] ?? 0,
                            fear: probs[2] ?? 0,
                            happy: probs[3] ?? 0,
                            neutral: probs[4] ?? 0, 
                            sad: probs[5] ?? 0,
                            surprise: probs[6] ?? 0 
                        };

                        setDebugEmotions(raw);

                        frameBuffer.current = [...frameBuffer.current.slice(-smoothingWindow + 1), raw];

                        if (now - lastEmitRef.current >= aggregationMs) {
                            const count = frameBuffer.current.length || 1;
                            const sums = frameBuffer.current.reduce((acc, curr) => ({
                                angry: acc.angry + curr.angry,
                                disgust: acc.disgust + curr.disgust,
                                fear: acc.fear + curr.fear,
                                happy: acc.happy + curr.happy,
                                neutral: acc.neutral + curr.neutral,
                                sad: acc.sad + curr.sad,
                                surprise: acc.surprise + curr.surprise,
                            }), { angry: 0, disgust: 0, fear: 0, happy: 0, neutral: 0, sad: 0, surprise: 0 });

                            const averaged: EmotionProbabilities = {
                                angry: sums.angry / count,
                                disgust: sums.disgust / count,
                                fear: sums.fear / count,
                                happy: sums.happy / count,
                                neutral: sums.neutral / count,
                                sad: sums.sad / count,
                                surprise: sums.surprise / count
                            };

                            lastEmitRef.current = now;
                            onPrediction(averaged);
                        }
                    } else {
                        setDebugEmotions(null);
                    }
                } catch (err) {
                    // 2. ERROR SUPPRESSION: Log but don't crash
                    // This catches the 'backend undefined' error if it happens during prediction
                    console.warn("AI Loop Glitch (recovering...):", err);
                }
            }
            rafRef.current = requestAnimationFrame(processFrame);
        };
        rafRef.current = requestAnimationFrame(processFrame);
    };

    const init = async () => {
        try {
            console.log("AI: Initializing...");
            setProgress(0.1);
            await tf.ready();
            
            console.log("AI: Loading BlazeFace...");
            faceDetectorRef.current = await blazeface.load();
            setProgress(0.5);

            console.log("AI: Loading Emotion Model...");
            modelRef.current = await tf.loadGraphModel(modelUrl);
            
            tf.tidy(() => {
                const dummy = tf.zeros([1, 48, 48, 1]);
                modelRef.current?.predict(dummy);
            });
            
            console.log("AI: Ready. Starting Loop.");
            setIsReady(true);
            setProgress(1);
            startProcessingLoop();

        } catch (err) {
            console.error("AI Init Error:", err);
            setError('Failed to load AI');
        }
    };

    init();

    return () => {
        isMounted = false;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        modelRef.current?.dispose();
    };
  }, [modelUrl]);

  return { isReady, progress, error, debugEmotions };
};